import get from 'lodash/get';
import tuner from '@tunein/web-tuner';

import { extractLotameInterestsFromNowPlaying } from '../utils/ads/lotameUtils';
import { isPodcastProfile, isProfile } from '../utils/guideItemTypes';
import { buildGuideItemPathnameWithLogger } from '../utils/guideItem/getGuideItemPathname';
import { doNotFetch } from '../constants/locationState';
import playerStatuses from '../utils/playerStatuses';
import web from '../constants/analytics/categoryActionLabel/web';
import { selectExperiment } from '../selectors/config';
import { IHEARTRADIO_ALBUM_ART_ENABLED } from '../constants/experiments/player';
import { fetchProfile } from './profile';
import { sendLotameInterests } from './mint';
import { determineInAppMessageDialogRequestBehavior } from './dialog';
import {
  logClientError,
  logStreamEvaluationSuccessTime,
  logStreamEvaluationFailTime,
  logTotalTuneSuccessTime,
  logTotalTuneFailTime,
  logPlayStartNext,
  logTotalTuneFailTimeWithAdBlocker,
  logCategoryActionLabel,
} from './logging';

export const PLAYING = 'PLAYING';
export const PLAYING_AD = 'PLAYING_AD';
export const CONNECTING = 'CONNECTING';
export const WAITING = 'WAITING';
export const NOW_PLAYING_CHANGED = 'NOW_PLAYING_CHANGED';
export const POSITION_CHANGED = 'POSITION_CHANGED';
export const MEDIA_ENDED = 'MEDIA_ENDED';
export const FAILED = 'FAILED';
export const QUALIFIED_TUNE = 'QUALIFIED_TUNE';
export const HANDLE_HTML_STREAM = 'HANDLE_HTML_STREAM';
export const NEXT_TUNE = 'NEXT_TUNE';
export const START_STREAM_EVALUATION = 'START_STREAM_EVALUATION';
export const CHANGE_PLAYBACK_RATE = 'CHANGE_PLAYBACK_RATE';
export const HANDLE_META = 'HANDLE_META';

export function changePlaybackRateEffect(rate, canChangePlaybackRate) {
  return {
    type: CHANGE_PLAYBACK_RATE,
    rate,
    canChangePlaybackRate,
  };
}

export function changePlaybackRateIfPossible(rate) {
  return (dispatch, getState) => {
    const defaultRate = 1.0;
    const { player, profiles } = getState();
    const primaryGuideId = get(player, 'nowPlaying.primaryGuideId');
    const playerStatus = get(player, 'playerStatus');
    const isPodcast = isPodcastProfile(get(profiles, primaryGuideId));
    const rateToUse = parseFloat(
      rate || get(player, 'playbackRate', defaultRate),
    );
    const canChangePlaybackRate = tuner.canChangePlaybackRate();

    if (!canChangePlaybackRate) {
      return dispatch(changePlaybackRateEffect(defaultRate, false));
    }

    if (!isPodcast) {
      tuner.changePlaybackRate(defaultRate);
      return dispatch(changePlaybackRateEffect(defaultRate, false));
    }

    if (
      playerStatus === playerStatuses.playing ||
      playerStatus === playerStatuses.connecting
    ) {
      tuner.changePlaybackRate(rateToUse);
    }

    // we only want to log if a rate has been specified by user selection
    if (rate) {
      dispatch(
        logCategoryActionLabel({
          category: web.category,
          action: `${rateToUse}`,
          label: web.labels.changePlaybackRate,
        }),
      );
    }

    return dispatch(changePlaybackRateEffect(rateToUse, true));
  };
}

export function playing() {
  return (dispatch, getState) => {
    const {
      tunedGuideId: guideId,
      streamEvaluationStart,
      itemToken,
      tuneRequestStart,
      hadPreroll,
    } = getState().player;

    const now = Date.now();

    if (streamEvaluationStart) {
      const time = now - streamEvaluationStart;

      dispatch(
        logStreamEvaluationSuccessTime({
          guideId,
          time,
          itemToken,
          hadPreroll,
        }),
      );
    }

    if (tuneRequestStart) {
      const time = now - tuneRequestStart;

      dispatch(
        logTotalTuneSuccessTime({ guideId, time, itemToken, hadPreroll }),
      );
    }

    return dispatch({ type: PLAYING }).then(() =>
      dispatch(changePlaybackRateIfPossible()),
    );
  };
}

export function playingAd() {
  return {
    type: PLAYING_AD,
  };
}

export function connecting(nextGuideId) {
  return {
    type: CONNECTING,
    tunedGuideId: nextGuideId,
  };
}

export function waiting() {
  return {
    type: WAITING,
  };
}

export function nowPlayingChanged(nowPlaying = {}) {
  return (dispatch, getState) => {
    const { primaryGuideId } = nowPlaying;
    const prevPrimaryGuideId = get(
      getState(),
      'player.nowPlaying.primaryGuideId',
    );
    if (prevPrimaryGuideId !== primaryGuideId) {
      dispatch(
        sendLotameInterests(extractLotameInterestsFromNowPlaying(nowPlaying)),
      );
    }

    const inAppMessageKey = get(nowPlaying, 'popup.destinationInfo.id');
    if (inAppMessageKey && prevPrimaryGuideId !== primaryGuideId) {
      dispatch(
        determineInAppMessageDialogRequestBehavior(
          inAppMessageKey,
          primaryGuideId,
        ),
      );
    }

    return dispatch({ type: NOW_PLAYING_CHANGED, nowPlaying });
  };
}

export function positionChanged(positionInfo) {
  return (dispatch, getState) => {
    const isMobile = getState().app.isMobile;
    return dispatch({
      type: POSITION_CHANGED,
      positionInfo,
      isMobile,
    });
  };
}

export function mediaEnded() {
  return {
    type: MEDIA_ENDED,
  };
}

export function streamFailed() {
  return (dispatch, getState) => {
    const {
      tunedGuideId: guideId,
      streamEvaluationStart,
      itemToken,
      hadPreroll,
    } = getState().player;

    const now = Date.now();

    if (streamEvaluationStart) {
      const time = now - streamEvaluationStart;

      dispatch(
        logStreamEvaluationFailTime({ guideId, time, itemToken, hadPreroll }),
      );
    }
  };
}

export function stationFailed(error = {}) {
  return (dispatch, getState) => {
    const {
      tunedGuideId: guideId,
      itemToken,
      tuneRequestStart,
      hadPreroll,
      hasAdBlocker,
    } = getState().player;

    const now = Date.now();

    if (tuneRequestStart) {
      const time = now - tuneRequestStart;

      dispatch(logTotalTuneFailTime({ guideId, time, itemToken, hadPreroll }));
      if (hasAdBlocker) {
        dispatch(
          logTotalTuneFailTimeWithAdBlocker({ guideId, time, itemToken }),
        );
      }
    }

    return dispatch({
      type: FAILED,
      failedWithHttp: error.failedWithHttp,
    });
  };
}

export function qualifiedTune() {
  return {
    type: QUALIFIED_TUNE,
  };
}

export function handleHtmlStream(externalHtmlPlayerUrl) {
  return {
    type: HANDLE_HTML_STREAM,
    externalHtmlPlayerUrl,
  };
}

export function handleStreamEvaluation(time) {
  return {
    type: START_STREAM_EVALUATION,
    streamEvaluationStart: time || Date.now(),
  };
}

// called from the tuner
export function nextTune({ nextGuideId: guideId, itemToken }, history) {
  return (dispatch) => {
    const nextTuneAction = {
      type: NEXT_TUNE,
      itemToken,
    };

    dispatch(
      logPlayStartNext({
        guideId,
        itemToken,
      }),
    );

    // NOTE/TODO: currently restricting redirect and now playing link updates to profile guide ids
    // only. This is to avoid unnecessary regressions for topic tunes. If we come to a point where
    // we want to adjust and redirect for topic tunes (e.g. update profile url with topicid, and
    // then update nowplaying link with topicid), we should change this to handle all types of
    // next guide ids generically.
    if (isProfile(guideId)) {
      // Perform a fresh profiles API fetch to get/update profile data, as well as allow us to
      // form the profile url
      return dispatch(fetchProfile({ guideId, itemToken }))
        .catch((error) => {
          dispatch(nextTuneAction);

          throw error;
        })
        .then(({ action }) => {
          // NOTE: this is the direct output structure from apiClientEffects from a resolved
          // request.
          const guideItem = action.payload.content;
          const redirectPathname = buildGuideItemPathnameWithLogger(guideItem)(
            (...args) => dispatch(logClientError(...args)),
          );

          dispatch({
            ...nextTuneAction,
            guideItemPathname: redirectPathname,
          });

          if (redirectPathname) {
            // Because we already performed a profiles API fetch above, we don't want to
            // perform a duplicate one. We'll use the `doNotFetch` instruction to prevent async
            // data loading from occurring once the route changes to the new profile page.
            history.replace({
              pathname: redirectPathname,
              state: {
                [doNotFetch]: true,
              },
            });
          }
        });
    }

    return dispatch(nextTuneAction);
  };
}

export function handleMeta(data) {
  const IMG_SCALE = 300;
  return (dispatch, getState) => {
    const meta = {};
    let extraData = {};

    // Search first object for TXXX regardless of its version key name
    const keys = Object.keys(data?.native);
    const txxx =
      data?.native[keys[0]] &&
      data.native[keys[0]].find((tag) => tag?.id?.match(/^TXXX/));
    if (txxx) {
      // Clean up and split the space separated key/value string (e.g. 'song_spot="M" TPID="0"')
      extraData = txxx.value
        .replace(/"/g, '')
        .split(' ')
        .reduce((acc, item) => {
          const [key, value] = item.split('=');
          const newObj = { ...acc, [key]: value };
          return newObj;
        }, {});
    } else {
      // Cannot rely on artist without song_spot (i.e. could be garbage ad data)
      return;
    }

    // If song_spot is "M" (music) and artist is known
    if (data?.common?.artist && extraData?.song_spot === 'M') {
      meta.title = `${data.common.artist} - ${data.common.title}`;
      // If we are allowed to use album art (flagged just in case)
      const isAlbumArtEnabled = selectExperiment(
        getState(),
        IHEARTRADIO_ALBUM_ART_ENABLED,
      );
      if (isAlbumArtEnabled) {
        // If TPID is not zero, use iHeart image scaler
        if (parseInt(extraData.TPID, 10) >= 0) {
          meta.image = `https://i.iheart.com/v3/catalog/track/${extraData.TPID}?ops=fit(${IMG_SCALE},${IMG_SCALE})`;
        }
      }
      // Stop the poller since we are getting info directly from ID3 metadata
      // The poller will restart on next tune.
      tuner.stopPoller();
      return dispatch({ type: HANDLE_META, meta });
    }
  };
}
